import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AuthContainer, AuthContent, AuthContentInner, AuthHeader, LayoutSplit, LoaderDots } from 'components';
import { AuthIntegrationMarketing } from 'components/Auth/AuthMarketing';
import { toRem } from 'design';
import useConnectIntegration from 'hooks/useConnectIntegration';
import { DeviceService } from 'lib';
import Utils from 'lib/Utils';
import { routeConstants } from 'routes';
import { loginRequest } from 'store/App';
import { AuthType, IAuthMFARequired } from 'types';
import { AuthMFAVerification } from '../Auth';

interface IMFARequired {
  required: boolean;
  mfa?: IAuthMFARequired;
}

const AltAuthContentInner = styled(AuthContentInner)`
  max-width: ${toRem(480)};
`;

export const ConnectIntegrationLoginGoogle: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showMFAVerify, setShowMFAVerify] = useState<IMFARequired>({ required: false });
  const { search } = useLocation();
  const googleCode = new URLSearchParams(search).get('code');
  const googleError = new URLSearchParams(search).get('error');
  const integration = useConnectIntegration();

  useEffect(() => {
    if (googleCode) {
      dispatch(
        loginRequest(
          {
            type: AuthType.GOOGLE,
            token: googleCode,
            deviceId: DeviceService.getDeviceId(),
            redirectUri: Utils.buildUrl(routeConstants.public.redirectGoogle.integrationLogin, [integration.name]),
          },
          onMFARequired,
          onFail,
          integration.name
        )
      );
    }
  }, [dispatch, googleCode]);

  useEffect(() => {
    if (googleError && integration) {
      history.push(`/connect/${integration.name}`);
    }
  }, [googleError, integration]);

  const onMFARequired = (mfa: IAuthMFARequired) => {
    setShowMFAVerify({ required: true, mfa });
  };

  const onFail = () => {
    history.push(`${routeConstants.public.invites.login}?message=no_account_created`);
  };

  const onMFASuccess = () => history.push(`/connect/${integration.name}/connections`);

  const mfaVerificationAuthHeader = (
    <AuthHeader
      logo={integration?.logo()}
      data-cy="auth-mfa-verify-header"
      title={integration?.title}
      subtitle="Enter the code sent to your mobile phone"
      onBack={() => history.replace(`/connect/${integration?.name}`)}
      value={`xxx-xxx-${showMFAVerify.mfa?.phoneLast4}`}
    />
  );

  return (
    <LayoutSplit
      left={
        <AuthContainer>
          <AuthContent>
            <AltAuthContentInner>
              {showMFAVerify && showMFAVerify.mfa ? <AuthMFAVerification authHeader={mfaVerificationAuthHeader} onSuccess={onMFASuccess} mfaRequired={showMFAVerify.mfa} /> : <LoaderDots size="large" />}
            </AltAuthContentInner>
          </AuthContent>
        </AuthContainer>
      }
      right={<AuthIntegrationMarketing integration={integration.name} />}
    />
  );
};
